import * as Logger from './logger.js';
import { HSComponent } from './component.js';
import { UserSession } from './session.js';
import * as Utils from './utils.js';

export class HSCard extends HSComponent {
    actionHandlers = {};
    static sseIdCounter = 0;
    sseSubscriptionId = 0;
    sseSubscriptionVersion = 0;
    subscribed = false;

    constructor() { 
        super(); 

        this.actionHandlers = {
            redirect:       (opts) => { Logger.info('redirecting to: ', opts); window.location.replace(opts.url); },
            open:           (opts) => { Logger.info('opening in new tab: ', opts); window.open(opts.url, '_blank'); },
            blade:          (opts) => this.emit({ ...opts, type: 'hs-load-blade' }),
            showTabContent: (opts) => this.emit({ ...opts, id: this.config.id, type: 'hs-show-tab-content' }),
            setUserState:   (opts) => { UserSession.set(opts.key, opts.state); this.emit({ type: 'hs-reload-data' }); },
            build:          (opts) => this.emit({ type: 'hs-build-widget', state: opts }),
        };
    }

    onLoad() {
    }

    onUnload() {
        super.onUnload();
        this.actionHandlers = {};
        // remove our SSE subscription, if any
        if (this.subscribed && this.sseSubscriptionId) {
            const subscriptions = UserSession.get('sseSubscriptions') || {};
            delete subscriptions[this.sseSubscriptionId];
            UserSession.set('sseSubscriptions', subscriptions);
            this.fetch(`/sse?unsubscribe=${UserSession.get('sseStreamId')}:${this.sseSubscriptionId}`);
        }
    }

    redraw() {}

    toggleEditMode(enabled) {
        super.toggleEditMode(enabled);
        requestAnimationFrame(() => {
            this.redraw();
        });
    }

    getState(opts) { return {}; }   // base class method, not used

    init(config) {
        super.init(config);
    }

    // Central action dispatcher used by all card subclasses.
    // opts: raw action config (action/path/state/url may be template strings).
    // context: per-card evaluation context (e.g. { row, col, rows } for tables).
    runAction(opts, context = {}) {
        const ctxKeys = Object.keys(context);
        const ctxVals = Object.values(context);
        const action = Utils.evaluate(opts.action, ctxKeys, ctxVals);
        Logger.debug("runAction: ", action, opts);
        const evaluated = {
            ...opts,
            action,
            path:  Utils.evaluate(opts.path,  ctxKeys, ctxVals),
            state: Utils.evaluate(opts.state, ctxKeys, ctxVals),
            url:   Utils.evaluate(opts.url,   [...ctxKeys, 'host', 'session'],
                                              [...ctxVals, new URL(window.location.href).hostname, UserSession]),
        };
        if (this.actionHandlers[action]) {
            this.actionHandlers[action](evaluated);
        } else {
            this.emit({ ...evaluated, ...context, type: action });
        }
    }
    // Base is a no-op so grid-wide search can safely broadcast to every card; HSTable overrides with the real filter behavior.
    search(str) {}
    setListView() { Logger.warn("card doesn't support list view"); }
    setGridView() { Logger.warn("card doesn't support grid view"); }
    setSelected(selected) { Logger.warn("card doesn't support setSelected"); }
    async reloadData() { Logger.warn("card doesn't support reloadData"); }
    updateData(obj) { Logger.warn("card doesn't support updateData"); }

    onSubscribed(subscription) {
        Logger.info("got SSE subscribed message: ", subscription);
        this.sseSubscriptionVersion = subscription.subscription_version;
    }

    onUpdate(update) {
        Logger.info("got SSE data update for card: ", update);
         //id: "1759784400000", obj: {Principal: 38, utc_timestamp_inms: '1759784400000'}, subscription_id: "1", subscription_version: 1
        if (update.subscription_version != this.sseSubscriptionVersion) {
            Logger.warn("discarding old SSE versioned data update for card: ", update);
            return;
        }
        //this.updateData(update.id, update.obj);
        this.updateData(update);
    }

    async getSourceData(src) {
        if (!src) {
            Logger.warn("no source data configured for card");
            return [];
        }
        if (Array.isArray(src)) {
            // FAKE: "src" is an array of fake data (legacy)
            Logger.debug("using static data: ", src);
            return src;
        }
        let path = null;
        if (typeof(src) !== 'object') {
            // SIMPLE: "src" is a string, process directly as the complete URL path
            path = Utils.evaluate(src, this);
        } else if (src.session) {
            // using session to provide the data
            const data = Utils.evaluate(src.session, [ 'session' ], [ UserSession ], this);
            if (data) {
                Logger.debug("using session data: ", data);
                return data;
            }
            Logger.warn("no session data found for path: ", src.session);
            return [];
        } else {
            // ADVANCED: "src" is an object, build the path from the separate config properties
            Logger.debug("building source path from config object: ", src);
            path = this.getSourcePath(src);
        }
        if (!path) {
            Logger.warn("no source path resolved for card");
            return [];
        }
        Logger.debug("fetching ", path);
        let json = [];
        const fetchMethod = src && src.method ? String(src.method).toUpperCase() : 'GET';
        let fetchBody = undefined;
        if (src && src.body && typeof src.body === 'object') {
            // Evaluate template expressions inside body values the same way src.path is evaluated,
            // so callers can use ${session.get('...')} expressions in POST body fields.
            // Utils.evaluate returns a parsed object (not a string) when the result starts with '{',
            // so do NOT call JSON.parse on it a second time.
            try {
                const evaluated = Utils.evaluate(JSON.stringify(src.body), [ 'session' ], [ UserSession ], this);
                fetchBody = typeof evaluated === 'string' ? JSON.parse(evaluated) : evaluated;
            } catch (_) {
                fetchBody = src.body;
            }
        }
        try {
            if (fetchMethod === 'POST') {
                Logger.info(`POST ${path} payload:`, JSON.stringify(fetchBody));
                json = await this.fetch(path, { method: 'POST', payload: fetchBody });
            } else {
                json = await this.fetch(path);
            }
        } catch(err) {
            Logger.error("Error fetching data: ", err);
            // If a subscription was registered locally but the subscribe request failed,
            // remove local registration to avoid stale handlers.
            if (this.subscribed && this.sseSubscriptionId) {
                const subscriptions = UserSession.get('sseSubscriptions') || {};
                delete subscriptions[this.sseSubscriptionId];
                UserSession.set('sseSubscriptions', subscriptions);
                this.subscribed = false;
                this.sseSubscriptionVersion = 0;
            }
        }

        // Builder/introspection endpoints can return a capabilities document with tabular field rows.
        if (!Array.isArray(json) && json && Array.isArray(json.field_rows)) {
            Logger.debug(`field rows for card ${this.config.type}::${this.config.id ?? this.config.title}: `, json.field_rows.slice(0, 25));
            return json.field_rows;
        }

        // Opt-in envelope unwrap via src.unwrapField
        if (src?.unwrapField && !Array.isArray(json) && json && Array.isArray(json[src.unwrapField])) {
            Logger.debug(`unwrap '${src.unwrapField}' for card ${this.config.type}::${this.config.id ?? this.config.title}: `, json[src.unwrapField].slice(0, 5));
            return json[src.unwrapField];
        }

        const preview = Array.isArray(json) ? json.slice(0, 25) : json;
        Logger.debug(`json for card ${this.config.type}::${this.config.id ?? this.config.title}: `, preview);
        const rows = Array.isArray(json) ? json : (json ? [ json ] : []);
        // Optional per-row derivation: src.computedFields is { fieldName: templateExpression }; each expression sees the row's fields as bindings.
        if (src && typeof src === 'object' && src.computedFields && typeof src.computedFields === 'object' && rows.length > 0) {
            for (const row of rows) {
                const keys = Object.keys(row);
                const vals = keys.map(k => row[k]);
                for (const [fieldName, expression] of Object.entries(src.computedFields)) {
                    let evaluated;
                    try {
                        evaluated = Utils.evaluate(expression, keys, vals);
                    } catch (_) {
                        evaluated = 0;
                    }
                    const asNumber = Number(evaluated);
                    // Default to 0 when the expression can't produce a number (e.g. row is missing a referenced field, so Utils.evaluate returns the raw template).
                    row[fieldName] = Number.isFinite(asNumber) ? asNumber : 0;
                }
            }
        }
        return rows;
    }

    getSourcePath(src) {
        let path = Utils.evaluate(src.path, [ 'session' ], [ UserSession ], this);
        path += (path.includes('?') ? '&' : '?') + '_card=' + encodeURIComponent(this.config.id || this.config.title || 'unknown');
        path += '&rid=' + Math.random();   // cache busting
        let filters = [];
        if (src.applyProviderFilters) {
            const isOrgScopedSource = (typeof path === 'string') && (
                path.includes('rollup.org.') ||
                path.includes('/rollup/org/') ||
                path.includes('/org/')
            );
            const orgs = UserSession.get('selected_orgs').length > 0 ? UserSession.get('selected_orgs') : [];
            Logger.info("orgs: ", orgs);
            const providers = UserSession.get('selected_providers').length > 0 ? UserSession.get('selected_providers') : [];
            if (isOrgScopedSource) {
                const orgIds = (Array.isArray(orgs) && orgs.length > 0)
                    ? orgs.map(o => o.id)
                    : Array.from(new Set((providers || []).map(p => p.orgid).filter(Boolean)));
                if (orgIds.length > 0) {
                    Logger.debug("applying org filters from state: ", orgIds);
                    filters.push([ '${obj.orgid}', 'in', orgIds ]);
                } else if (UserSession.get('userinfo').usertype == 'ops' && Array.isArray(orgs) && orgs.length > 0) {
                    Logger.debug("applying orgs from state via query param: ", orgs);
                    path += '&' + 'orgs=' + orgs.map(o => o.id).join(',');
                } else {
                    Logger.debug("no org selected for org-scoped source");
                }
            } else if (Array.isArray(providers) && providers.length > 0) {
                // [ "${obj.providerid}", "in", [ "provider1", "provider2" ] ]
                Logger.debug("applying providers from state: ", providers);
                filters.push([ '${obj.providerid}', 'in', providers.map(p => p.id) ]);
            } else if (UserSession.get('userinfo').usertype == 'ops' && Array.isArray(orgs) && orgs.length > 0) {
                Logger.debug("applying orgs from state: ", orgs);
                path += '&' + 'orgs=' + orgs.map(o => o.id).join(',');
            } else if (Array.isArray(orgs) && orgs.length > 0) {
                // Non-ops user selected an org but no explicit providers: use the org-filtered providers list (kept in sync by hs-org-select) so provider-scoped sources honor the org context.
                const filteredProviders = UserSession.get('filtered_providers') || [];
                if (filteredProviders.length > 0) {
                    Logger.debug("applying org-filtered providers from state: ", filteredProviders);
                    filters.push([ '${obj.providerid}', 'in', filteredProviders.map(p => p.id) ]);
                } else {
                    Logger.debug("selected orgs present but no filtered_providers available");
                }
            } else {
                Logger.debug("no org/provider selected");
            }
        }
        if (src.applyTimeScopeFilters) {
            const scopeOrder = ['day', 'week', 'month', 'quarter', 'all'];
            let timeScope = src.timeScope || UserSession.get('timeScope') || 'all';
            if (src.minTimeScope) {
                const minIdx = scopeOrder.indexOf(src.minTimeScope);
                const curIdx = scopeOrder.indexOf(timeScope);
                if (minIdx > -1 && curIdx > -1 && curIdx < minIdx) {
                    timeScope = src.minTimeScope;
                }
            }
            if (src.timeSeries) {
                if (timeScope == 'all') {
                    // keep historical coverage while satisfying required time-options validation
                    path += '&endtime=' + Date.now();
                } else if (timeScope == 'quarter') {
                    path += '&starttime=' + Utils.getCurrentUTCQuarter();
                } else if (timeScope == 'month') {
                    path += '&starttime=' + Utils.last30DaysUTC();
                } else if (timeScope == 'week') {
                    path += '&starttime=' + Utils.last7DaysUTC();
                } else {
                    path += '&starttime=' + Utils.last24HoursUTC();
                }
            } else {
                if (timeScope == 'all') {
                    // no filter
                } else if (timeScope == 'quarter') {
                    path += '&timestamp=' + Utils.getCurrentUTCQuarter();
                } else if (timeScope == 'month') {
                    path += '&timestamp=' + Utils.last30DaysUTC();
                } else if (timeScope == 'week') {
                    path += '&timestamp=' + Utils.last7DaysUTC();
                } else {
                    path += '&timestamp=' + Utils.last24HoursUTC();
                }
            }
        } else {
            if (src.starttime !== undefined && src.starttime !== null) {
                path += '&starttime=' + Utils.evaluate(src.starttime, [ 'Utils', 'session' ], [ Utils, UserSession ], this);
            }
            if (src.endtime !== undefined && src.endtime !== null) {
                path += '&endtime=' + Utils.evaluate(src.endtime, [ 'Utils', 'session' ], [ Utils, UserSession ], this);
            }
            if (src.timestamp !== undefined && src.timestamp !== null) {
                path += '&timestamp=' + Utils.evaluate(src.timestamp, [ 'Utils', 'session' ], [ Utils, UserSession ], this);
            }
        }
        if (filters.length > 0) {
            if (src.filter) {
                filters.push(src.filter);
            }
            // NOTE: we double stringify to escape the double quotes
            if (filters.length == 1) {
                Logger.debug("single implied filter: ", filters);
                path += '&filter=' + JSON.stringify(filters[0]);
            } else {
                Logger.debug("combined implied filters: ", filters);
                const combinedFilters = filters.reduce((acc, curr, idx) => {
                    acc.push(curr);
                    if (idx < filters.length - 1) {
                        acc.push('and');
                    }
                    return acc;
                }, []);
                path += '&filter=' + JSON.stringify(combinedFilters);
            }
        } else if (src.filter) {
            Logger.debug("supplied filter: ", src.filter);
            path += '&filter=' + JSON.stringify(src.filter);
        }
        if (src.post_filter) {
            Logger.debug("post filter: ", src.post_filter);
            path += '&post_filter=' + JSON.stringify(src.post_filter);
        }
        if (src.fields) {
            path += '&fields=' + src.fields.join(',');
        }
        if (src.aggregate) {
            path += '&aggregate=' + JSON.stringify(src.aggregate);
        }
        if (src.derive) {
            path += '&derive=' + JSON.stringify(src.derive);
        }
        if (src.groupby) {
            path += '&groupby=' + [].concat(src.groupby).join(',');
        }
        if (src.adornments) {
            path += '&adornments=' + JSON.stringify(src.adornments);
        }
        if (src.flags) {
            path += '&' + src.flags.join('&');
        }
        if (src.subscribe) {
            if (UserSession.get('sseStreamId')) {
                if (!this.sseSubscriptionId) {
                    // increment the SSE subscription id counter and assign this Card the next value
                    HSCard.sseIdCounter++;
                    this.sseSubscriptionId = HSCard.sseIdCounter;
                }
                path += `&subscribe=${UserSession.get('sseStreamId')}:${this.sseSubscriptionId}`;
                const subscriptions = UserSession.get('sseSubscriptions') || {};
                subscriptions[this.sseSubscriptionId] = {
                    onSubscribed: this.onSubscribed.bind(this),
                    onUpdate: this.onUpdate.bind(this),
                    resubscribe: async (activeStreamId) => {
                        const previousStreamId = UserSession.get('sseStreamId');
                        try {
                            UserSession.set('sseStreamId', activeStreamId);
                            const nextPath = await this.getSourceData(src);
                            if (nextPath) {
                                await this.fetch(nextPath);
                            }
                        } finally {
                            UserSession.set('sseStreamId', previousStreamId);
                        }
                    },
                };
                UserSession.set('sseSubscriptions', subscriptions);
                Logger.info("registered SSE subscription: " + this.sseSubscriptionId);
                this.subscribed = true;
            } else {
                Logger.warn("SSE subscription not available because no SSE session is connected");
            }
        }

        return path;
    }

    setActionHandler(action, func) {
        this.actionHandlers[action] = func;
    }
}

